import { ArrowLeftSolid } from '@allocab/allocab-icons';
import { AskCancelOutput, ConfirmCancelInput } from '@allocab/allocab-passenger-shared-types';
import { MouseEvent, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { SecondaryButton } from '../components/button/SecondaryButton';
import { ErrorPage } from '../components/error-page/ErrorPage';
import { Spinner } from '../components/Spinner';
import { CancelTripModal } from '../components/trip/cancel-trip-modal/CancelTripModal';
import { TripDetail } from '../components/trip/TripDetail';
import { TripHelp } from '../components/trip/TripHelp';
import { TripOptions } from '../components/trip/TripOptions';
import { UpdateTripButton } from '../components/trip/update-trip/UpdateTripButton';
import { TripMap } from '../components/TripMap';
import { constants } from '../constants';
import { useAskCancellation } from '../hooks/useAskCancellation';
import { useConfirmCancellation } from '../hooks/useConfirmCancellation';
import { useFetchTrip } from '../hooks/useFetchTrip';
import { useFetchTripGuideDownload } from '../hooks/useFetchTripGuideDownload';
import { t } from '../services/translation/translation';
import { TripExtraProvider } from '../services/trip-extra/TripExtraProvider';
import { getRefreshInterval, getStepForTrip } from '../utils/trip';

export const TripRoute = () => {
  const searchingDriverRef = useRef(null);
  const [cancelRequest, setCancelRequest] = useState<AskCancelOutput | null>(null);
  const { tripKey } = useParams();
  invariant(tripKey, 'Expected tripKey');
  const { data: trip, isLoading } = useFetchTrip(tripKey, {
    staleTime: 1000,
    refetchInterval: getRefreshInterval,
    refetchOnWindowFocus: true,
  });
  const askCancellationMutation = useAskCancellation(tripKey);
  const confirmCancellationMutation = useConfirmCancellation(tripKey);
  const location = useLocation();
  const navigate = useNavigate();

  const { data: dataGuide } = useFetchTripGuideDownload(tripKey);

  const handleAskCancellation = async () => {
    askCancellationMutation.mutate(
      { key: tripKey },
      {
        onSuccess: data => setCancelRequest(data),
      },
    );
  };

  const handleConfirmCancellation = async (input: ConfirmCancelInput) => {
    confirmCancellationMutation.mutate(input, { onSuccess: () => setCancelRequest(null) });
  };

  const handleBack = (e: MouseEvent) => {
    e.preventDefault();
    if (location.key === 'default') {
      navigate('/trips');
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
        <Spinner className="scale-[3]" />
      </div>
    );
  }

  if (!trip) {
    return <ErrorPage />;
  }

  const step = getStepForTrip(trip);

  return (
    <>
      <Helmet>
        <title>{t('trip-route.meta-title', { tripId: trip.id })}</title>
      </Helmet>
      <div className="flex flex-col xl:flex-row-reverse h-[calc(100vh_-_4rem)] overflow-x-auto">
        <div className="w-full xl:w-[608px] order-2 xl:order-1 xl:h-full xl:overflow-y-auto p-4">
          <TripDetail
            searchingDriverRef={searchingDriverRef}
            trip={trip}
            isCreatingNote={false}
            dataGuide={dataGuide}
          />
          {!constants.isHotels &&
          (step === 'BOOKING_CONFIRMED' ||
            step === 'SEARCHING_DRIVER' ||
            step === 'DRIVER_APPROACHING' ||
            step === 'DRIVER_IN_PLACE') ? (
            <TripOptions trip={trip} />
          ) : null}
          <div>
            <TripHelp anonymous={false} trip={trip} />
          </div>
          {trip.state !== 'CANCELLED' && trip.state !== 'DONE' ? <UpdateTripButton trip={trip} /> : null}

          {trip.state != 'CANCELLED' &&
          trip.state != 'DONE' &&
          trip.state != 'ONDUTY' &&
          // "Booking" account
          trip.account.key !== 'Passengeraccount_10910906688' ? (
            <>
              <SecondaryButton
                isLoading={askCancellationMutation.isLoading}
                className="w-full mt-2"
                onClick={handleAskCancellation}
              >
                {t('common.cancel')}
              </SecondaryButton>
            </>
          ) : null}
        </div>
        <div className="w-full order-1 xl:order-2 min-h-[50%] xl:h-full relative">
          <div className="h-full" ref={searchingDriverRef}>
            {trip ? (
              <TripExtraProvider>
                <TripMap trip={trip} />
              </TripExtraProvider>
            ) : null}
          </div>
          <button
            className="absolute flex top-4 left-4  text-sm items-center gap-x-1 text-text_secondary bg-background p-2 rounded-sm"
            onClick={handleBack}
          >
            <ArrowLeftSolid width={16} height={16} className="fill-text_secondary" />
            <p>{t('nav.trips')}</p>
          </button>
        </div>
      </div>
      {cancelRequest ? (
        <CancelTripModal
          trip={trip}
          cancelRequest={cancelRequest}
          onClose={() => setCancelRequest(null)}
          onConfirm={handleConfirmCancellation}
          isLoading={confirmCancellationMutation.isLoading}
        />
      ) : null}
    </>
  );
};
